import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext';
import { useAuthFetch } from '../hooks/useAuthFetch';

import '../stili/ProgrammaGaraForm.css';

const API_URL = process.env.REACT_APP_API_URL;

export default function ProgrammaGaraForm() {
  const [nomeEvento, setNomeEvento] = useState('');
  const [idEvento, setIdEvento] = useState(null);

  const [stagioni, setStagioni] = useState([]);
  const [idStagione, setIdStagione] = useState('');
  const [visibileBacheca, setVisibileBacheca] = useState(false);

  const [gara, setGara] = useState({
    idCategoria: '',
    idStaffResponsabile: '',
    gara: '',
    dataGara: '',
    oraGara: '',
    indirizzo: '',
    luogo: ''
  });

  const [gare, setGare] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [programmaCreato, setProgrammaCreato] = useState(false);

  const [categorie, setCategorie] = useState([]);
  const [loadingCategorie, setLoadingCategorie] = useState(true);

  const [staffList, setStaffList] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(true);

  const { user } = useAuth();
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  useEffect(() => {
    if (!user?.accessToken) navigate('/login');
  }, [user, navigate]);

  const fetchStagioni = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/stagioni`);
      if (!res.ok) throw new Error('Errore caricamento stagioni');
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setStagioni(list);

      const attiva = list.find((s) => s.attiva === true);
      if (attiva) setIdStagione(String(attiva.id));
    } catch (e) {
      toast.error(e.message);
    }
  }, [authFetch]);

  const fetchCategorie = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/categorie`);
      if (!res.ok) throw new Error('Errore caricamento categorie');
      const data = await res.json();
      setCategorie(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Errore nel caricamento categorie: ' + e.message);
    } finally {
      setLoadingCategorie(false);
    }
  }, [authFetch]);

  const fetchStaff = useCallback(async () => {
    try {
      const res = await authFetch(
        `${API_URL}/api/admin/staff?stato=ATTIVO&allenatore=true&page=0&size=200&sort=cognome&dir=ASC`
      );
      if (!res.ok) throw new Error('Errore caricamento staff');

      const data = await res.json();
      const lista = Array.isArray(data?.content) ? data.content : [];
      setStaffList(lista);
    } catch (e) {
      toast.error('Errore nel caricamento staff: ' + e.message);
      setStaffList([]);
    } finally {
      setLoadingStaff(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (!user?.accessToken) return;
    fetchStagioni();
    fetchCategorie();
    fetchStaff();
  }, [fetchStagioni, fetchCategorie, fetchStaff, user]);

  const categoriaLabelById = useMemo(() => {
    const m = new Map();
    categorie.forEach((c) => m.set(String(c.id), c.nome));
    return m;
  }, [categorie]);

  const staffLabelById = useMemo(() => {
    const m = new Map();
    staffList.forEach((s) => {
      m.set(String(s.id), `${s.cognome ?? ''} ${s.nome ?? ''}`.trim());
    });
    return m;
  }, [staffList]);

  if (!user?.accessToken) return null;

  const resetFormGara = () => {
    setGara({
      idCategoria: '',
      idStaffResponsabile: '',
      gara: '',
      dataGara: '',
      oraGara: '',
      indirizzo: '',
      luogo: ''
    });
  };

  const creaEvento = async () => {
    toast.dismiss();

    if (!nomeEvento.trim()) {
      toast.warning('Inserisci il nome evento');
      return;
    }
    if (!idStagione) {
      toast.warning('Seleziona una stagione');
      return;
    }

    try {
      const response = await authFetch(`${API_URL}/api/eventi-gara`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nomeEvento: nomeEvento.trim(),
          idStagione: Number(idStagione),
          visibileBacheca: !!visibileBacheca
        })
      });

      if (!response.ok) {
        let msg = 'Errore creazione evento';
        try {
          const err = await response.json();
          msg = err.message || msg;
        } catch {}
        throw new Error(msg);
      }

      const created = await response.json();
      setIdEvento(created.id);
      toast.success('Evento creato');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const aggiungiGara = () => {
    toast.dismiss();

    if (
      !gara.idCategoria ||
      !gara.idStaffResponsabile ||
      !gara.gara ||
      !gara.dataGara ||
      !gara.oraGara ||
      !gara.indirizzo ||
      !gara.luogo
    ) {
      toast.warning('Compila tutti i campi gara');
      return;
    }

    const formattedGara = {
      ...gara,
      gara: gara.gara.toUpperCase(),
      indirizzo: gara.indirizzo.toUpperCase(),
      luogo: gara.luogo.toUpperCase()
    };

    if (editingIndex !== null) {
      const updated = [...gare];
      updated[editingIndex] = formattedGara;
      setGare(updated);
      setEditingIndex(null);
    } else {
      setGare([...gare, formattedGara]);
    }

    resetFormGara();
  };

  const modificaGara = (index) => {
    setGara(gare[index]);
    setEditingIndex(index);
  };

  const eliminaGara = (index) => {
    setGare(gare.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      resetFormGara();
    }
  };

  const creaProgramma = async () => {
    toast.dismiss();

    if (!idEvento) {
      toast.warning('Crea prima l’evento');
      return;
    }
    if (gare.length === 0) {
      toast.warning('Aggiungi almeno una gara');
      return;
    }

    const gareDaSalvare = gare.map((g) => ({
      idEventoGara: idEvento,
      idCategoria: Number(g.idCategoria),
      idStaffResponsabile: Number(g.idStaffResponsabile),
      avversario: g.gara,
      dataGara: g.dataGara,
      oraGara: g.oraGara,
      indirizzo: g.indirizzo,
      luogo: g.luogo
    }));

    try {
      const response = await authFetch(`${API_URL}/api/gare/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gareDaSalvare)
      });

      if (!response.ok) {
        let msg = 'Errore nella creazione delle gare';
        try {
          const err = await response.json();
          msg = err.message || msg;
        } catch {}
        throw new Error(msg);
      }

      setProgrammaCreato(true);
      toast.success('Programma gare creato con successo');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const scaricaPdf = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/eventi-gara/${idEvento}/pdf`, {
        method: 'GET'
      });
      if (!response.ok) throw new Error('Errore nel download del PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'programma_gare.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore durante il download del PDF: ' + error.message);
    }
  };

  const gareOrdinate = [...gare].sort((a, b) =>
    String(a.idCategoria).localeCompare(String(b.idCategoria))
  );

  return (
    <div className="pg-container" translate="no">
      {!idEvento ? (
        <div className="pg-card">
          <h2 className="pg-title">Crea Evento</h2>

          <div className="pg-evento-top-row">
            <select
              className="pg-select"
              value={idStagione}
              onChange={(e) => setIdStagione(e.target.value)}
            >
              <option value="">Seleziona stagione</option>
              {stagioni.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nome}{s.attiva ? ' (ATTIVA)' : ''}
                </option>
              ))}
            </select>

            <label className="pg-checkbox-inline">
              <input
                type="checkbox"
                checked={visibileBacheca}
                onChange={(e) => setVisibileBacheca(e.target.checked)}
              />
              Visibile in bacheca
            </label>
          </div>

          <input
            type="text"
            value={nomeEvento}
            onChange={(e) => setNomeEvento(e.target.value.toUpperCase())}
            placeholder="Nome evento"
            className="pg-input"
          />

          <div className="pg-button-row pg-button-row-left">
            <button className="pg-btn" onClick={creaEvento}>Crea Evento</button>
            <button className="pg-btn" onClick={() => navigate('/gestionale')}>Torna al Gestionale</button>
          </div>
        </div>
      ) : (
        <div className="pg-card">
          <h2 className="pg-title">
            {programmaCreato ? 'Programma Gare' : 'Aggiungi Gare'} per l'evento: {nomeEvento}
          </h2>

          {loadingCategorie && <p>Caricamento categorie...</p>}
          {loadingStaff && <p>Caricamento staff...</p>}

          {!programmaCreato && (
            <div className="pg-form-grid">
              {!loadingCategorie && (
                <>
                  <div className="pg-form-field">
                    <label className="pg-label">Categoria</label>
                    <select
                      className="pg-select"
                      value={gara.idCategoria}
                      onChange={(e) => setGara({ ...gara, idCategoria: e.target.value })}
                    >
                      <option value="">Seleziona categoria</option>
                      {categorie.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.nome}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pg-form-field">
                    <label className="pg-label">Staff responsabile</label>
                    <select
                      className="pg-select"
                      value={gara.idStaffResponsabile}
                      onChange={(e) => setGara({ ...gara, idStaffResponsabile: e.target.value })}
                    >
                      <option value="">Seleziona staff responsabile</option>
                      {staffList.map((s) => (
                        <option key={s.id} value={s.id}>
                          {`${s.cognome ?? ''} ${s.nome ?? ''}`.trim()}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="pg-form-field">
                <label className="pg-label">Squadra avversaria</label>
                <input
                  type="text"
                  placeholder="Squadra Avversaria"
                  value={gara.gara}
                  onChange={(e) => setGara({ ...gara, gara: e.target.value.toUpperCase() })}
                  className="pg-input"
                />
              </div>

              <div className="pg-form-field">
                <label className="pg-label">Data gara</label>
                <input
                  type="date"
                  value={gara.dataGara}
                  onChange={(e) => setGara({ ...gara, dataGara: e.target.value })}
                  className="pg-input"
                />
              </div>

              <div className="pg-form-field">
                <label className="pg-label">Ora gara</label>
                <input
                  type="time"
                  value={gara.oraGara}
                  onChange={(e) => setGara({ ...gara, oraGara: e.target.value })}
                  className="pg-input"
                />
              </div>

              <div className="pg-form-field pg-form-field-wide">
                <label className="pg-label">Indirizzo</label>
                <input
                  type="text"
                  placeholder="Indirizzo"
                  value={gara.indirizzo}
                  onChange={(e) => setGara({ ...gara, indirizzo: e.target.value.toUpperCase() })}
                  className="pg-input"
                />
              </div>

              <div className="pg-form-field">
                <label className="pg-label">Luogo</label>
                <select
                  value={gara.luogo}
                  onChange={(e) => setGara({ ...gara, luogo: e.target.value.toUpperCase() })}
                  className="pg-select"
                >
                  <option value="">Seleziona luogo</option>
                  <option value="CASA">CASA</option>
                  <option value="TRASFERTA">TRASFERTA</option>
                  <option value="NEUTRO">NEUTRO</option>
                </select>
              </div>
            </div>
          )}

          {!programmaCreato && (
            <div className="pg-button-row pg-button-row-left">
              <button className="pg-btn" onClick={aggiungiGara}>
                {editingIndex !== null ? 'Modifica Gara' : 'Aggiungi Gara'}
              </button>
            </div>
          )}

          <div className="pg-cards-container">
            {gareOrdinate.length === 0 ? (
              <div className="pg-empty-card">Nessuna gara inserita</div>
            ) : (
              gareOrdinate.map((g, i) => (
                <div className="pg-gara-card" key={i}>
                  <div className="pg-gara-card-header">
                    <div className="pg-gara-card-title">
                      {g.luogo === 'CASA'
                        ? `MONTEROTONDO 1935 vs ${g.gara}`
                        : g.luogo === 'TRASFERTA'
                          ? `${g.gara} vs MONTEROTONDO 1935`
                          : g.gara}
                    </div>

                    <div className="pg-gara-card-badge">
                      {categoriaLabelById.get(String(g.idCategoria)) || '-'}
                    </div>
                  </div>

                  <div className="pg-gara-card-grid">
                    <div className="pg-gara-card-item">
                      <span className="pg-gara-card-label">Data</span>
                      <span>{new Date(g.dataGara).toLocaleDateString('it-IT')}</span>
                    </div>

                    <div className="pg-gara-card-item">
                      <span className="pg-gara-card-label">Ora</span>
                      <span>{g.oraGara?.slice(0, 5)}</span>
                    </div>

                    <div className="pg-gara-card-item">
                      <span className="pg-gara-card-label">Luogo</span>
                      <span>{g.luogo}</span>
                    </div>

                    <div className="pg-gara-card-item">
                      <span className="pg-gara-card-label">Staff responsabile</span>
                      <span>{staffLabelById.get(String(g.idStaffResponsabile)) || '-'}</span>
                    </div>

                    <div className="pg-gara-card-item pg-gara-card-item-full">
                      <span className="pg-gara-card-label">Indirizzo</span>
                      <span>{g.indirizzo}</span>
                    </div>
                  </div>

                  {!programmaCreato && (
                    <div className="pg-card-actions">
                      <button className="pg-btn" onClick={() => modificaGara(i)}>Modifica</button>
                      <button className="pg-btn" onClick={() => eliminaGara(i)}>Elimina</button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="pg-button-row">
            {!programmaCreato && (
              <button className="pg-btn" onClick={creaProgramma}>
                Crea Programma Gara
              </button>
            )}
            {programmaCreato && (
              <>
                <button className="pg-btn" onClick={scaricaPdf}>Scarica PDF</button>
                <button className="pg-btn" onClick={() => navigate('/gestionale')}>
                  Torna al Gestionale
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}